/**
 * A deterministic seeded pseudo-random number generator for the hand-drawn map
 * renderer (Spatial Map System R11.1, R11.2, R11.6). The ENTIRE renderer's
 * randomness — the sketchy-line jitter, the organic-hull wobble, the asset
 * scatter — flows through ONE instance of this, seeded with a node's fixed
 * `renderSeed`, so the same map always draws identically (R11.2 "the map is a
 * stable artifact, not a kaleidoscope").
 *
 * CRITICAL (R11.6): the renderer must NEVER call `Math.random()` — a single
 * stray call breaks determinism silently. A lint/review guard
 * (`no-math-random.test.ts`) asserts no renderer source references `Math.random`.
 * This generator is the SOLE sanctioned source of randomness.
 *
 * Algorithm: mulberry32 — a tiny, fast, well-distributed 32-bit PRNG. Pure and
 * self-contained (no deps), so the draw plan is reproducible across machines and
 * builds. Two instances seeded identically emit the exact same sequence.
 */

/** A seeded RNG: deterministic, stateful, and fully reproducible from its seed. */
export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    // Normalize to a 32-bit unsigned integer so a float/negative seed is stable.
    this.state = (Math.floor(seed) >>> 0) || 1;
  }

  /** The next float in [0, 1) — the mulberry32 step. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** The next float in [min, max). */
  nextInRange(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** A symmetric jitter in [-magnitude, +magnitude) — the sketch wobble. */
  jitter(magnitude: number): number {
    return this.nextInRange(-magnitude, magnitude);
  }

  /** The next integer in [min, max] inclusive. */
  nextInt(min: number, max: number): number {
    return Math.floor(this.nextInRange(min, max + 1));
  }
}
